export interface NormalizedListPage<T> {
  data: T[];
  total: number;
  success: boolean;
  current?: number;
  pageSize?: number;
  message?: string;
}

/**
 * 分页接口返回值的类型表达 —— 两种形态都可能出现：
 *
 *   1. **摊平形态**（经 umi 响应拦截器，应用内绝大多数情况）
 *      { success, data: T[], total, current, pageSize, message? }
 *   2. **原始信封形态**（未过拦截器的路径：raw fetch/XHR/SSE、单测、mock）
 *      { success, data: { data: T[], total, current, pageSize, success? } }
 *
 * 消费方两种写法都可以：直接做双形态容错读取，或一律过 normalizeListPage() 归一。
 * 新增分页服务请复用本类型，不要再内联一份信封形状。
 * total / current / pageSize 含 string：后端存在以字符串下发数值的情况。
 */
export interface PagedResponse<T> {
  success: boolean;
  message?: string;
  /** 摊平形态下是行数组；原始信封形态下是分页对象 */
  data:
    | T[]
    | {
        data: T[];
        total?: number | string;
        current?: number | string;
        pageSize?: number | string;
        success?: boolean;
      };
  /** 以下三项只在摊平形态下出现在顶层 */
  total?: number | string;
  current?: number | string;
  pageSize?: number | string;
}

type RecordValue = Record<string, unknown>;
const isRecord = (value: unknown): value is RecordValue =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const integer = (value: unknown, minimum: number): number | undefined => {
  if (typeof value !== 'number' && (typeof value !== 'string' || !value.trim())) {
    return undefined;
  }
  const result = Number(value);
  return Number.isSafeInteger(result) && result >= minimum ? result : undefined;
};

/**
 * Keep server totals attached to their rows across Umi's flattened page,
 * Result<PageResult>, Result<IPage>, and a retained HTTP response shell.
 * An explicitly returned zero is meaningful and must not fall back to row count.
 */
export function normalizeListPage<T = any>(response: unknown): NormalizedListPage<T> {
  const shells: RecordValue[] = [];
  let value = response;
  let rows: T[] = [];
  while (isRecord(value) && !shells.includes(value)) {
    shells.push(value);
    if (Array.isArray(value.records)) {
      rows = value.records as T[];
      break;
    }
    if (Array.isArray(value.data)) {
      rows = value.data as T[];
      break;
    }
    value = value.data;
  }
  if (Array.isArray(response)) rows = response as T[];
  const nearestFirst = [...shells].reverse();
  const numberFrom = (fields: string[], minimum: number) => {
    for (const shell of nearestFirst) {
      for (const field of fields) {
        const parsed = integer(shell[field], minimum);
        if (parsed !== undefined) return parsed;
      }
    }
    return undefined;
  };
  return {
    data: rows,
    total: numberFrom(['total'], 0) ?? rows.length,
    success: !shells.some((shell) => shell.success === false),
    current: numberFrom(['current', 'pageNum'], 1),
    pageSize: numberFrom(['pageSize', 'size'], 1),
    message: nearestFirst.find((shell) => typeof shell.message === 'string')?.message as string | undefined,
  };
}

/** Preserve the existing interceptor contract, including non-page Result objects. */
export function flattenPageResult(response: unknown): unknown {
  if (!isRecord(response) || !response.success || !isRecord(response.data)) return response;
  const page = response.data;
  if (!Array.isArray(page.data) || typeof page.total !== 'number') return response;
  return { ...response, ...page, success: response.success && page.success !== false };
}

/** For existing tree / local-filter selectors that require every eligible row. */
export async function fetchAllListPages<T = any>(
  fetchPage: (params: { current: number; pageSize: number }) => Promise<unknown>,
): Promise<NormalizedListPage<T>> {
  const rows: T[] = [];
  let total = 0;
  let previousIds: string | undefined;
  for (let current = 1; current <= 200; current += 1) {
    const page = normalizeListPage<T>(await fetchPage({ current, pageSize: 100 }));
    if (!page.success) throw new Error(page.message || '列表加载失败');
    total = page.total;
    const ids = page.data.map((row: any) => row?.id);
    const signature = ids.length && ids.every((id) => id != null) ? JSON.stringify(ids) : undefined;
    if (signature && signature === previousIds) throw new Error('列表接口未正确翻页，请刷新后重试');
    previousIds = signature;
    rows.push(...page.data);
    if (rows.length >= total) return { data: rows, total, success: true };
    if (!page.data.length) throw new Error('列表数据发生变化，未能完整加载，请刷新后重试');
  }
  throw new Error('列表超过完整加载上限，请缩小查询范围');
}
